import React from 'react';
import type { PracticeArea } from '../../types';
import FlippableCard from '../ui/FlippableCard';
import { ArrowRight } from 'react-feather';
import LocalizedLink from '../ui/LocalizedLink';

interface PracticeAreaCardProps {
  area: Pick<PracticeArea, 'slug' | 'title' | 'subtitle' | 'imageUrl' | 'flipCardText'>;
  index?: number;
}

const PracticeAreaCard: React.FC<PracticeAreaCardProps> = ({ area, index = 0 }) => {
  const generateImageProps = (url: string) => {
    if (!url || !url.includes('pexels.com')) {
      return { src: url };
    }
    try {
      const baseUrl = url.split('?')[0];
      const baseParams = "auto=compress&cs=tinysrgb&w=600&fm=webp&q=75";
      const newUrl = new URL(url);
      newUrl.searchParams.set('q', '75');
      newUrl.searchParams.set('fm', 'webp');
      
      const widths = [400, 600];
      const srcSet = widths.map(w => {
          newUrl.searchParams.set('w', w.toString());
          return `${newUrl.href} ${w}w`;
      }).join(', ');

      newUrl.searchParams.set('w', '600');
      return {
        src: newUrl.href,
        srcSet,
      };
    } catch (e) {
      return { src: url };
    }
  };

  const imageProps = generateImageProps(area.imageUrl);

  const Front = (
    <div className="relative h-full overflow-hidden rounded-3xl bg-white/5 border border-white/10 shadow-lg">
      <img 
        {...imageProps}
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        alt={area.title} 
        className="absolute inset-0 w-full h-full object-cover" 
        width="400"
        height="300"
        loading="lazy"
      />
      <div className="practice-card-image-overlay" />
      <div className="practice-card-title-overlay">
         <h3 className="text-2xl font-bold font-serif text-white drop-shadow-lg">{area.title}</h3>
      </div>
    </div>
  );

  const Back = (
    <div className="h-full flex flex-col justify-between items-center text-center p-6 rounded-3xl bg-brand-dark border border-white/10 shadow-lg glass-effect overflow-hidden">
      <div>
        <h3 className="text-2xl font-bold font-serif text-brand-gold-light">{area.title}</h3>
        <p className="mt-2 text-white/80">{area.flipCardText || area.subtitle}</p>
      </div>
      <div className="mt-4 text-sm font-semibold text-brand-gold-light group-hover:text-white transition-colors duration-300 flex items-center justify-center gap-2">
          Learn More <ArrowRight size={16} />
      </div>
    </div>
  );
  
  return (
    <LocalizedLink to={`/practice-areas/${area.slug}`} className="group block aspect-[4/3]">
      <FlippableCard
        frontContent={Front}
        backContent={Back}
      />
    </LocalizedLink>
  );
};

export default PracticeAreaCard;